import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import type {
  CreateTreeProblemTypeInput,
  UpdateTreeProblemTypeInput,
} from '@lib/input/tree-problem-type.input'
import type {
  CreateProblemSolutionInput,
  UpdateProblemSolutionInput,
} from '@lib/input/problem-solution.input'
import { treeProblemTypeService } from '@/services/TreeProblemTypeService'
import { treeProblemTypeKeys } from '@/hooks/queryKeys'

/**
 * The problem directory.
 *
 * A long `staleTime`, like the species catalog: the list changes a handful
 * of times a year and the survey screen needs it on every mount to draw a
 * picker. Refetching it each time somebody opens a map would be work spent on
 * a list that has not moved.
 */
export function useTreeProblemTypes(): UseQueryResult<TreeProblemTypeDto[]> {
  return useQuery({
    queryKey: treeProblemTypeKeys.list(),
    queryFn: async () => (await treeProblemTypeService.list()).problemTypes,
    staleTime: 5 * 60_000,
  })
}

/**
 * Changing the directory invalidates only the directory.
 *
 * Deliberately unlike species, which also invalidate every cached tree: a tree
 * carries its species' MARK inline so a pin can draw itself without a second
 * lookup, so renaming a species leaves cached trees holding stale artwork. A
 * tree carries only a problem's `typeId`, and the name, description and
 * solution are read from this list at render time — so there is nothing stale
 * to clear, and invalidating every survey would be work for no change.
 */
function useProblemTypeMutation<TInput>(
  run: (input: TInput) => Promise<TreeProblemTypeDto>,
): UseMutationResult<TreeProblemTypeDto, Error, TInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: treeProblemTypeKeys.all })
    },
  })
}

export function useCreateTreeProblemType(): UseMutationResult<
  TreeProblemTypeDto,
  Error,
  CreateTreeProblemTypeInput
> {
  return useProblemTypeMutation((input) => treeProblemTypeService.create(input))
}

export function useUpdateTreeProblemType(): UseMutationResult<
  TreeProblemTypeDto,
  Error,
  { id: string; input: UpdateTreeProblemTypeInput }
> {
  return useProblemTypeMutation(({ id, input }) =>
    treeProblemTypeService.update(id, input),
  )
}

export function useArchiveTreeProblemType(): UseMutationResult<
  TreeProblemTypeDto,
  Error,
  { id: string; archived: boolean }
> {
  return useProblemTypeMutation(({ id, archived }) =>
    treeProblemTypeService.setArchived(id, archived),
  )
}

/**
 * Solution mutations.
 *
 * Each returns the whole problem type, and each invalidates the LIST anyway.
 * Seeding the cache from the response would mean splicing one row back into a
 * list the server orders — archived last, then by sort order — so the merge
 * would be a second implementation of that ordering, kept in step by hand.
 * A refetch is one round trip and cannot drift.
 */
export function useCreateProblemSolution(): UseMutationResult<
  TreeProblemTypeDto,
  Error,
  { problemTypeId: string; input: CreateProblemSolutionInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ problemTypeId, input }) =>
      treeProblemTypeService.createSolution(problemTypeId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: treeProblemTypeKeys.all })
    },
  })
}

export function useUpdateProblemSolution(): UseMutationResult<
  TreeProblemTypeDto,
  Error,
  { id: string; input: UpdateProblemSolutionInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }) =>
      treeProblemTypeService.updateSolution(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: treeProblemTypeKeys.all })
    },
  })
}

export function useArchiveProblemSolution(): UseMutationResult<
  TreeProblemTypeDto,
  Error,
  { id: string; archived: boolean }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, archived }) =>
      treeProblemTypeService.setSolutionArchived(id, archived),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: treeProblemTypeKeys.all })
    },
  })
}
